/**
 * Engagements — master/detail.
 *
 * The old preview rendered raw UUIDs in the assignments table and asked for an
 * asset UUID by hand. Both are now resolved to names via lookup maps built
 * from the field-user and asset lists, and the modal picks from dropdowns.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  Briefcase,
  Calendar,
  MapPin,
  Plus,
  Receipt,
  Search,
  Timer,
  Truck,
  User,
  Users,
} from 'lucide-react';

import { operationsApi, type EngagementAssignment, type EngagementDetail } from '../api';
import { fieldUserApi } from '@/features/auth/api';
import { SmartLoading, StatusPill } from '@/lib/ui';

type FieldUserOption = {
  id: string;
  employee_id: string;
  full_name: string;
  status: string;
};

const fmtDate = (s: string | null | undefined) =>
  s
    ? new Date(s).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

export function EngagementsPage() {
  const [items, setItems] = useState<EngagementDetail[]>([]);
  const [selected, setSelected] = useState<EngagementDetail | null>(null);
  const [assignments, setAssignments] = useState<EngagementAssignment[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Shared lookups — the assignments table needs names, not UUIDs.
  const fieldUsers = useQuery({ queryKey: ['field-users'], queryFn: fieldUserApi.list });
  const assets = useQuery({
    queryKey: ['assets', 'for-engagements'],
    queryFn: () => operationsApi.listAssets({ limit: 500 }),
  });

  const userById = useMemo(() => {
    const m = new Map<string, FieldUserOption>();
    ((fieldUsers.data ?? []) as FieldUserOption[]).forEach((u) => m.set(u.id, u));
    return m;
  }, [fieldUsers.data]);

  const assetById = useMemo(() => {
    const m = new Map<string, { code: string; name: string }>();
    (assets.data?.items ?? []).forEach((a) => m.set(a.id, { code: a.code, name: a.name }));
    return m;
  }, [assets.data]);

  const refresh = async () => {
    setLoading(true);
    try {
      const list = await operationsApi.listEngagements();
      setItems(list);
      setSelected((cur) => (cur ? (list.find((e) => e.id === cur.id) ?? cur) : cur));
    } finally {
      setLoading(false);
    }
  };

  // Initial load only — `refresh` is re-created each render and is called
  // explicitly everywhere else it's needed.
  useEffect(() => {
    void refresh();
  }, []);

  useEffect(() => {
    if (selected) {
      operationsApi.listAssignments(selected.id).then(setAssignments);
    } else {
      setAssignments([]);
    }
  }, [selected]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((e) =>
      [e.name, e.code, e.customer_name ?? ''].join(' ').toLowerCase().includes(q),
    );
  }, [items, search]);

  return (
    <div className="flex h-full min-h-0">
      {/* ── List ───────────────────────────────────────────────────── */}
      <aside className="flex w-[360px] shrink-0 flex-col border-r border-border">
        <div className="border-b border-border px-4 py-3">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-base font-semibold">
              <Briefcase className="h-4 w-4 text-primary" />
              Engagements
              {items.length > 0 && (
                <span className="rounded-full bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                  {items.length}
                </span>
              )}
            </h2>
            <button className="btn-primary btn-sm" onClick={() => setShowCreate(true)}>
              <Plus className="h-3.5 w-3.5" />
              New
            </button>
          </div>
          <div className="relative mt-3">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, code or customer…"
              className="input-search text-sm"
              aria-label="Search engagements"
            />
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          {loading ? (
            <SmartLoading className="px-4 py-8 text-center text-sm text-muted-foreground" />
          ) : filtered.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-muted-foreground">
              {items.length === 0 ? 'No engagements yet.' : 'Nothing matches your search.'}
            </p>
          ) : (
            filtered.map((e) => {
              const active = selected?.id === e.id;
              return (
                <button
                  key={e.id}
                  onClick={() => setSelected(e)}
                  className={`block w-full border-b border-border/60 px-4 py-3 text-left transition-colors ${
                    active
                      ? 'border-l-2 border-l-primary bg-primary/5 pl-[14px]'
                      : 'hover:bg-muted/30'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="truncate font-medium">{e.name}</span>
                    <StatusPill status={e.status} />
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="font-mono">{e.code}</span>
                    {e.customer_name && (
                      <>
                        <span aria-hidden>·</span>
                        <span className="truncate">{e.customer_name}</span>
                      </>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </aside>

      {/* ── Detail ─────────────────────────────────────────────────── */}
      <div className="min-w-0 flex-1 overflow-auto">
        {!selected ? (
          <div className="grid h-full place-items-center p-6 text-center">
            <div>
              <Briefcase className="mx-auto h-8 w-8 text-muted-foreground/50" />
              <p className="mt-3 text-sm font-medium">No engagement selected</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Pick one from the list to see its details and crew.
              </p>
            </div>
          </div>
        ) : (
          <EngagementDetailPane
            engagement={selected}
            assignments={assignments}
            userById={userById}
            assetById={assetById}
            onRefresh={async () => {
              await refresh();
              setAssignments(await operationsApi.listAssignments(selected.id));
            }}
          />
        )}
      </div>

      {showCreate && (
        <EngagementCreateModal
          onClose={() => setShowCreate(false)}
          onCreated={async () => {
            setShowCreate(false);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

function EngagementDetailPane({
  engagement,
  assignments,
  userById,
  assetById,
  onRefresh,
}: {
  engagement: EngagementDetail;
  assignments: EngagementAssignment[];
  userById: Map<string, FieldUserOption>;
  assetById: Map<string, { code: string; name: string }>;
  onRefresh: () => void;
}) {
  const [showAssign, setShowAssign] = useState(false);
  const activeCount = assignments.filter((a) => a.is_active).length;

  const billing =
    engagement.billing_rate_minor != null
      ? `${(engagement.billing_rate_minor / 100).toLocaleString()} ${engagement.currency ?? ''} / ${engagement.billing_unit ?? '—'}`
      : '—';

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="truncate text-2xl font-semibold tracking-tight">{engagement.name}</h1>
            <StatusPill status={engagement.status} />
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            <span className="font-mono">{engagement.code}</span>
            {engagement.customer_name && <> · {engagement.customer_name}</>}
          </p>
        </div>
        <button className="btn-secondary" onClick={() => setShowAssign(true)}>
          <Plus className="h-4 w-4" />
          Assign crew
        </button>
      </div>

      {engagement.description && (
        <p className="mt-4 rounded-lg border border-border bg-muted/25 px-4 py-3 text-sm leading-relaxed">
          {engagement.description}
        </p>
      )}

      {/* Stat cards */}
      <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat icon={Receipt} label="Billing" value={billing} />
        <Stat
          icon={Timer}
          label="Expected duration"
          value={
            engagement.expected_work_unit_minutes != null
              ? `${engagement.expected_work_unit_minutes} min`
              : '—'
          }
        />
        <Stat icon={Users} label="Active crew" value={String(activeCount)} />
        <Stat
          icon={MapPin}
          label="Site"
          value={
            engagement.site_latitude != null
              ? `${engagement.site_latitude.toFixed(4)}, ${engagement.site_longitude?.toFixed(4)}`
              : 'Not set'
          }
          hint={engagement.auto_geofence ? 'Auto-geofence on' : undefined}
        />
      </div>

      {/* Facts */}
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FactRow
          icon={Calendar}
          label="Runs"
          value={`${fmtDate(engagement.starts_on)} → ${engagement.ends_on ? fmtDate(engagement.ends_on) : 'open-ended'}`}
        />
        <FactRow icon={User} label="Contact" value={engagement.customer_contact ?? '—'} />
      </div>

      {/* Assignments */}
      <div className="mt-8">
        <div className="mb-3 flex items-baseline justify-between">
          <h2 className="text-base font-semibold">Crew</h2>
          <span className="text-xs text-muted-foreground">
            {assignments.length} assignment{assignments.length === 1 ? '' : 's'}
          </span>
        </div>

        {assignments.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border px-4 py-10 text-center">
            <Users className="mx-auto h-6 w-6 text-muted-foreground/50" />
            <p className="mt-2 text-sm text-muted-foreground">
              Nobody assigned yet. Use <strong>Assign crew</strong> to add a field user.
            </p>
          </div>
        ) : (
          <div className="data-table-wrapper">
            <div className="data-table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Field user</th>
                    <th>Asset</th>
                    <th>Role</th>
                    <th>Window</th>
                    <th className="num"></th>
                  </tr>
                </thead>
                <tbody>
                  {assignments.map((a) => {
                    const user = userById.get(a.field_user_id);
                    const asset = a.asset_id ? assetById.get(a.asset_id) : undefined;
                    return (
                      <tr key={a.id} className={a.is_active ? '' : 'opacity-55'}>
                        <td>
                          <div className="font-medium">{user?.full_name ?? 'Unknown user'}</div>
                          <div className="font-mono text-xs text-muted-foreground">
                            {user?.employee_id ?? a.field_user_id.slice(0, 8)}
                          </div>
                        </td>
                        <td>
                          {asset ? (
                            <span className="inline-flex items-center gap-1.5">
                              <Truck className="h-3.5 w-3.5 text-muted-foreground" />
                              <span className="font-mono text-xs">{asset.code}</span>
                              <span className="text-muted-foreground">{asset.name}</span>
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td>{a.role_label ?? <span className="text-muted-foreground">—</span>}</td>
                        <td className="muted text-xs">
                          {fmtDate(a.starts_on)} → {a.ends_on ? fmtDate(a.ends_on) : 'open'}
                        </td>
                        <td className="actions">
                          {a.is_active ? (
                            <button
                              className="btn-ghost btn-sm text-danger hover:text-danger"
                              onClick={async () => {
                                await operationsApi.deactivateAssignment(a.id);
                                onRefresh();
                              }}
                            >
                              Remove
                            </button>
                          ) : (
                            <span className="text-xs text-muted-foreground">inactive</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {showAssign && (
        <AssignmentModal
          engagementId={engagement.id}
          assignedUserIds={
            new Set(assignments.filter((a) => a.is_active).map((a) => a.field_user_id))
          }
          onClose={() => setShowAssign(false)}
          onCreated={async () => {
            setShowAssign(false);
            onRefresh();
          }}
        />
      )}
    </div>
  );
}

// ── Small presentational pieces ────────────────────────────────────────────
function Stat({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: typeof Receipt;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-surface p-3">
      <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className="mt-1.5 truncate text-sm font-medium" title={value}>
        {value}
      </div>
      {hint && <div className="mt-0.5 text-xs text-success">{hint}</div>}
    </div>
  );
}

function FactRow({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Calendar;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-2.5 rounded-lg border border-border/60 px-3 py-2 text-sm">
      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="text-muted-foreground">{label}</span>
      <span className="ml-auto truncate font-medium" title={value}>
        {value}
      </span>
    </div>
  );
}

function ErrorNote({ children }: { children: React.ReactNode }) {
  return (
    <p
      role="alert"
      className="flex items-start gap-2 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger"
    >
      <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>{children}</span>
    </p>
  );
}

// ── Modals ─────────────────────────────────────────────────────────────────
function EngagementCreateModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [billingUnit, setBillingUnit] = useState<'HOUR' | 'DAY' | 'JOB' | 'KM' | ''>('');
  const [billingRate, setBillingRate] = useState('');
  const [expectedMins, setExpectedMins] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await operationsApi.createEngagement({
        code: code.trim(),
        name: name.trim(),
        customer_name: customerName.trim() || null,
        billing_unit: billingUnit || null,
        billing_rate_minor: billingRate ? Math.round(Number(billingRate) * 100) : null,
        expected_work_unit_minutes: expectedMins ? Number(expectedMins) : null,
      });
      onCreated();
    } catch (e) {
      setError((e as Error).message || 'Failed to create engagement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <form onSubmit={onSubmit} className="modal space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <Briefcase className="h-5 w-5 text-primary" />
            New engagement
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {error && <ErrorNote>{error}</ErrorNote>}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Code" required>
            <input
              required
              className="input font-mono"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="SITE-01"
            />
          </Field>
          <Field label="Name" required>
            <input
              required
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Marina Tower — Phase 2"
            />
          </Field>
          <Field label="Customer" wide>
            <input
              className="input"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
            />
          </Field>
          <Field label="Billing unit">
            <select
              className="input"
              value={billingUnit}
              onChange={(e) => setBillingUnit(e.target.value as typeof billingUnit)}
            >
              <option value="">—</option>
              <option>HOUR</option>
              <option>DAY</option>
              <option>JOB</option>
              <option>KM</option>
            </select>
          </Field>
          <Field label="Rate">
            <input
              type="number"
              step="0.01"
              min="0"
              className="input"
              value={billingRate}
              onChange={(e) => setBillingRate(e.target.value)}
              placeholder="0.00"
            />
          </Field>
          <Field label="Expected work-unit duration (minutes)" wide>
            <input
              type="number"
              min="0"
              className="input"
              value={expectedMins}
              onChange={(e) => setExpectedMins(e.target.value)}
            />
          </Field>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" disabled={saving} className="btn-primary">
            {saving ? 'Creating…' : 'Create engagement'}
          </button>
        </div>
      </form>
    </div>
  );
}

function AssignmentModal({
  engagementId,
  assignedUserIds,
  onClose,
  onCreated,
}: {
  engagementId: string;
  assignedUserIds: Set<string>;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [fieldUserId, setFieldUserId] = useState('');
  const [assetId, setAssetId] = useState('');
  const [roleLabel, setRoleLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const users = useQuery({ queryKey: ['field-users'], queryFn: fieldUserApi.list });
  const assets = useQuery({
    queryKey: ['assets', 'for-engagements'],
    queryFn: () => operationsApi.listAssets({ limit: 500 }),
  });

  // Someone already on this engagement shouldn't be offered again.
  const available = ((users.data ?? []) as FieldUserOption[]).filter(
    (u) => !assignedUserIds.has(u.id),
  );

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fieldUserId) {
      setError('Select a field user.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await operationsApi.createAssignment({
        engagement_id: engagementId,
        field_user_id: fieldUserId,
        asset_id: assetId || undefined,
        role_label: roleLabel.trim() || undefined,
      });
      onCreated();
    } catch (e) {
      setError((e as Error).message || 'Failed to create assignment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <form onSubmit={onSubmit} className="modal space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <Users className="h-5 w-5 text-primary" />
            Assign crew
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {error && <ErrorNote>{error}</ErrorNote>}

        <Field label="Field user" required wide>
          <select
            required
            className="input"
            value={fieldUserId}
            onChange={(e) => setFieldUserId(e.target.value)}
            disabled={users.isLoading}
          >
            <option value="">
              {users.isLoading
                ? 'Loading…'
                : available.length === 0
                  ? 'Everyone is already assigned'
                  : 'Select a field user…'}
            </option>
            {available.map((u) => (
              <option key={u.id} value={u.id}>
                {u.employee_id} · {u.full_name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Asset" wide>
          <select
            className="input"
            value={assetId}
            onChange={(e) => setAssetId(e.target.value)}
            disabled={assets.isLoading}
          >
            <option value="">{assets.isLoading ? 'Loading…' : 'No asset'}</option>
            {(assets.data?.items ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.code} · {a.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Role" wide>
          <input
            className="input"
            value={roleLabel}
            onChange={(e) => setRoleLabel(e.target.value)}
            placeholder="driver, helper, supervisor…"
          />
        </Field>

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" disabled={saving} className="btn-primary">
            {saving ? 'Assigning…' : 'Assign'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  children,
  wide,
  required,
}: {
  label: string;
  children: React.ReactNode;
  wide?: boolean;
  required?: boolean;
}) {
  return (
    <label className={'flex flex-col gap-1.5 text-sm ' + (wide ? 'col-span-2' : '')}>
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
        {required && <span className="ml-0.5 text-danger">*</span>}
      </span>
      {children}
    </label>
  );
}
